import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { DeliveryOption } from '../../types';
import './AdminFormStyles.css';

interface DeliveryOptionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  deliveryOption?: DeliveryOption | null;
}

const DeliveryOptionFormModal: React.FC<DeliveryOptionFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  deliveryOption
}) => {
  const [formData, setFormData] = useState({
    icon: '',
    name_bn: '',
    name_en: '',
    cost: '',
    description_bn: '',
    description_en: '',
    is_active: true,
    display_order: 0
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (deliveryOption) {
      setFormData({
        icon: deliveryOption.icon || '',
        name_bn: deliveryOption.name_bn || '',
        name_en: deliveryOption.name_en || '',
        cost: deliveryOption.cost?.toString() || '',
        description_bn: deliveryOption.description_bn || '',
        description_en: deliveryOption.description_en || '',
        is_active: deliveryOption.is_active ?? true,
        display_order: deliveryOption.display_order || 0
      });
    } else {
      setFormData({
        icon: '',
        name_bn: '',
        name_en: '',
        cost: '',
        description_bn: '',
        description_en: '',
        is_active: true,
        display_order: 0
      });
    }
    setErrors({});
  }, [deliveryOption, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.name_bn.trim()) newErrors.name_bn = 'Bangla name is required';
    if (!formData.name_en.trim()) newErrors.name_en = 'English name is required';
    if (!formData.cost || Number(formData.cost) < 0) newErrors.cost = 'Valid cost is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = {
        icon: formData.icon || null,
        name_bn: formData.name_bn,
        name_en: formData.name_en,
        cost: Number(formData.cost),
        description_bn: formData.description_bn,
        description_en: formData.description_en,
        is_active: formData.is_active,
        display_order: Number(formData.display_order)
      };

      await onSave(submitData);
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setErrors({ submit: 'Failed to save delivery option. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={deliveryOption ? 'Edit Delivery Option' : 'Add New Delivery Option'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="icon">
              Icon (Emoji)
              <span className="help-text">Example: 🚚 📦 ⚡ 🏍️</span>
            </label>
            <input
              type="text"
              id="icon"
              name="icon"
              value={formData.icon}
              onChange={handleChange}
              placeholder="Enter emoji icon"
              maxLength={10}
            />
            {errors.icon && <span className="error-message">{errors.icon}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="cost">
              Delivery Cost (৳) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="cost"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              placeholder="Enter cost amount"
              min="0"
              step="0.01"
              required
            />
            {errors.cost && <span className="error-message">{errors.cost}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name_bn">
              Name (Bangla) <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name_bn"
              name="name_bn"
              value={formData.name_bn}
              onChange={handleChange}
              placeholder="বাংলায় নাম লিখুন"
              required
            />
            {errors.name_bn && <span className="error-message">{errors.name_bn}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="name_en">
              Name (English) <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name_en"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              placeholder="Enter English name"
              required
            />
            {errors.name_en && <span className="error-message">{errors.name_en}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="description_bn">
              Description (Bangla)
            </label>
            <textarea
              id="description_bn"
              name="description_bn"
              value={formData.description_bn}
              onChange={handleChange}
              placeholder="বিস্তারিত বর্ণনা (ঐচ্ছিক)"
              rows={3}
            />
            {errors.description_bn && <span className="error-message">{errors.description_bn}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description_en">
              Description (English)
            </label>
            <textarea
              id="description_en"
              name="description_en"
              value={formData.description_en}
              onChange={handleChange}
              placeholder="Detailed description (optional)"
              rows={3}
            />
            {errors.description_en && <span className="error-message">{errors.description_en}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="display_order">
              Display Order
              <span className="help-text">Lower numbers appear first</span>
            </label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              min="0"
            />
            {errors.display_order && <span className="error-message">{errors.display_order}</span>}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <span>Active (visible to customers)</span>
            </label>
          </div>
        </div>

        {errors.submit && (
          <div className="form-error">
            {errors.submit}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (deliveryOption ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DeliveryOptionFormModal;
